import { FC, useCallback, useContext, useEffect, useState } from 'react';
import * as types from '@models/types';
import LoaderImage from '@assets/loader.gif';

// API
import * as url from '@api/requests';

// Providers
import { CountriesContext } from '@providers/Countries';
import { LoadingContext } from '@providers/Loading';
import { ProfileContext } from '@providers/Profile';

// Components
import { DropdownItemType } from '@components/Dropdown';
import { SelectMultiple, SelectSingle } from '@components/Input/Select';
import Table from '@components/Table';
/*import LineChart from '@components/Charts/LineChart';*/
import ColumnChart from '@components/Charts/ColumnChart';
import TopSkills from "@components/CountryDetails/components/TopSkills";
import TreemapChart from '@components/Charts/TreemapChart';
import { ArrowCircleDown, ArrowCircleUp, ArrowForwardIos } from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Button from '@components/Button';

// Utils
import { countrySortingFunction, getHubAccess } from '@helpers/utils';
import { SkillTypes, WSSkillGroups } from '@helpers/enums';
import './CompareColumn.scss';
import axios, { CancelTokenSource } from 'axios';
import { DefaultTimelines, Timelines } from '@config/timelines';

// Used to fill Options dropdown
const options = [
    { id: "1", title: "Sectors" },
    { id: "2", title: "Occupations" },
    { id: "3", title: "Specializations" }
];

// Used to fill Components dropdown
const components = [
    { id: "1", title: "Top Skills" },
    /*{ id: "2", title: "Historic Job Posts" },*/
    // { id: "3", title: "Top Employers" },
    // { id: "4", title: "Tree Map" }
    //{ id: "5", title: "Fastest growing sectors" },
    //{ id: "6", title: "Fastest growing ISCO2 occupations" },
    //{ id: "7", title: "Fastest growing ISCO4 occupations" }
];

const topOccupationsBySectorsTableCols = [
    {
        key: 'occupationCategoryName',
        label: 'Occupation name',
    },
    {
        key: 'iscO2Code',
        label: 'ISCO2 code',
    },
    {
        key: 'percentage',
        label: 'Market share',
    },
]



// Used to share data between parent and child component
export type CompareColumnOptionType = {
    preSelectedProfile?: types.SelectedProfileTypes;
    preSelectedRegion?: DropdownItemType;
    preSelectedCountries?: DropdownItemType[];
    preSelectedOptions?: DropdownItemType;
    preSelectedComponents?: DropdownItemType[];
    dropdownProvinces?: types.SelectedProfileChildrenTypes[];
};

export type CompareColumnNewType = {
    index: number;
    preSelectedOptions?: CompareColumnOptionType;
    handleChangePreSelectedOptions?: (compareColumnOptions: CompareColumnOptionType) => void
};



const CompareColumnNew: FC<CompareColumnNewType> = ({ index, preSelectedOptions, handleChangePreSelectedOptions }) => {
    // Context Models
    const { setIsLoading } = useContext(LoadingContext);
    const { regionList, setRegionList, regionDetails, setRegionDetails, countryList, setCountryList, setCountryDetails } = useContext(CountriesContext);
    const {
        sectorsData, setSectorsData,
        occupationsData, setOccupationsData,
        specializationsData, setSpecializationsData,
        timelinesData, setTimelinesData,
    } = useContext(ProfileContext);

    const [topOccupationsBySectors, setTopOccupationsBySectors] = useState<types.TopOccupationsBySectorsTypes[] | undefined>([]);
    const [totalJobPosts, setTotalJobPosts] = useState<types.TotalJobPostsTypes | undefined>([]);

    const [topEmployers, setTopEmployers] = useState<types.TopEmployersByQuarterTypes[]>([]);
    const [treeMapInfo, setTreeMapInfo] = useState<types.TreeMapInfoTypes>({ sectors: [], occupations: [] });
    const [topSkills, setTopSkills] = useState<types.TopSkillsGroupsTypes[] | undefined>([]);
    const [countriesList, setCountriesList] = useState<DropdownItemType[]>([]);
    const [provinceList, setProvinceList] = useState<types.SelectedProfileChildrenTypes[]>([]);
    const [getProvinceCancelToken, setGetProvinceCancelToken] = useState<CancelTokenSource>();
    const [topSkillGroup, setTopSkillGroup] = useState<string>(WSSkillGroups.HardSkill);
    const [skillType, setSkillType] = useState<string>(SkillTypes.ESCO);


    // Fields Models
    const [selectedRegion, setSelectedRegion] = useState<DropdownItemType>();
    const [selectedCountries, setSelectedCountries] = useState<DropdownItemType[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<DropdownItemType>();
    const [selectedProfile, setSelectedProfile] = useState<types.SelectedProfileTypes>({
        provinces: [],
        sector: [],
        occupation: [],
        specializations: [],
        fromTimeline: { title: "", id: "" },
        toTimeline: { title: "", id: "" },
        isEntryLevel: false
    });
    const [selectedComponents, setSelectedComponents] = useState<DropdownItemType[]>(components);
    const [loadProvinces, setLoadProvinces] = useState<boolean>(false);
    const [indexOfFromTimeline, setIndexOfFromTimeline] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

    // Used to fill topEmployersTable columns
    const [topEmployersTableCols, setTopEmployersTableCols] = useState<any[]>([])

    // Used to be sent to the parent component Compare.tsx
    const [newCompareColumnOptions, setNewCompareColumnOptions] = useState<CompareColumnOptionType>({});
    const [sectorAndOccupationInfo, setSectorAndOccupationInfo] = useState<types.SectorAndOccupationTypes | undefined>({ sectors: [], occupations: [] });



    // Used to fetch all needed data from APIs
    useEffect(() => {
        if (preSelectedOptions && preSelectedOptions.preSelectedCountries && preSelectedOptions.preSelectedCountries.length > 0) {
            preSelectedOptions.dropdownProvinces && setProvinceList(preSelectedOptions.dropdownProvinces);
            preSelectedOptions.preSelectedProfile && setSelectedProfile(preSelectedOptions.preSelectedProfile);
            preSelectedOptions.preSelectedRegion && setSelectedRegion(preSelectedOptions.preSelectedRegion);
            preSelectedOptions.preSelectedCountries && setSelectedCountries(preSelectedOptions.preSelectedCountries);
            preSelectedOptions.preSelectedOptions && setSelectedOptions(preSelectedOptions.preSelectedOptions);
            preSelectedOptions.preSelectedComponents && setSelectedComponents(preSelectedOptions.preSelectedComponents);
        }
        else {
            async function fetchData() {
                setIsLoading(true);

                const [regionsErr, regionsResponse] = await url.getRegions();
                const [countryErr, countryResponse] = await url.getCountries();
                const [countryDetailsErr, countryDetailsResponse] = await url.getCountryDetails();
                const [sectorsErr, incomingSectorsData] = await url.getSectorsList();
                const [occupationErr, incomingOccupationsData] = await url.getOccupationList();
                const [specializationsErr, incomingSpecializationsData] = await url.getSpecializationList();

                setIsLoading(false);

                if (regionsErr) console.warn(regionsErr);
                if (countryErr) console.warn(countryErr);
                if (countryDetailsErr) console.warn(countryDetailsErr);
                if (sectorsErr) console.warn(sectorsErr);
                if (occupationErr) console.warn(occupationErr);
                if (specializationsErr) console.warn(specializationsErr);

                setRegionDetails && setRegionDetails(regionsResponse);
                setCountryList && setCountryList(countryResponse.result.sort(countrySortingFunction));
                setCountryDetails && setCountryDetails(countryDetailsResponse.result);
                setSectorsData && setSectorsData(incomingSectorsData);
                setOccupationsData && setOccupationsData(incomingOccupationsData);
                setSpecializationsData && setSpecializationsData(incomingSpecializationsData);
            }

            fetchData();
        }
    }, [setCountryList, setCountryDetails, setIsLoading, setSectorsData, setRegionList]); // eslint-disable-line

    // Get Provinces according to the selected country
    const getProvinces = useCallback(async (countries: DropdownItemType[]) => {
        // Check if another province is running then cancel it
        if (getProvinceCancelToken)
            getProvinceCancelToken.cancel();

        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        setGetProvinceCancelToken(source);

        var tempProvinceList: types.SelectedProfileChildrenTypes[] = [];
        if (countries && countries.length > 0) {
            setLoadProvinces(true);
            var hubIds = countries.map((e: any) => e.id);
            const [provinceErr, provinceResponse] = await url.getLocationsByCountry(hubIds, source.token);
            if (!provinceErr && !provinceResponse)
                return;
            if (provinceErr) console.warn(provinceErr);
            tempProvinceList = provinceResponse;
            setProvinceList && setProvinceList(tempProvinceList);
            setLoadProvinces(false);
        }
        else
            setProvinceList(tempProvinceList);

        if (selectedProfile.provinces && selectedProfile.provinces.length > 0) {
            var temp = [...selectedProfile.provinces];
            var cleanedProvinces = temp.filter(value => { return tempProvinceList.filter(p => p.title === value.title).length > 0 });
            var cleanedProvincesStr = cleanedProvinces.map(v => { return v.title }).sort();
            var selectedProfileProvincesStr = selectedProfile.provinces.map(v => { return v.title }).sort();
            if (JSON.stringify(cleanedProvincesStr) !== JSON.stringify(selectedProfileProvincesStr)) {
                setSelectedProfile((prevState: types.SelectedProfileTypes) => ({
                    ...prevState,
                    provinces: cleanedProvinces
                }));
            }
        }
    }, [setProvinceList, selectedProfile.provinces, getProvinceCancelToken]);

    // Used to handle the change of the mutliselect components dropdown
    const handleChangeComponents = useCallback((event: any, currentTitle: string) => {
        setFormSubmitted(false)
        if (currentTitle === "reset") {
            setSelectedComponents([]);
        } else if (currentTitle === "all") {
            setSelectedComponents(components);
        } else {
            setSelectedComponents(event.target.value);
        }
    }, []);

    const handleChangeTimeline = (event: any) => {
        setFormSubmitted(false)
        if (!timelinesData) return;

        let profileKey: string
        let profileResult: types.SelectedProfileChildrenTypes = { title: "", id: "" }
        if (event.target === "reset") {
            profileKey = event.name
            profileResult = { title: "", id: "" }
        } else {
            const { value, name } = event.target
            const selectedObj = timelinesData.length ? timelinesData.find((e: any) => e.id === value) : null
            profileKey = name
            profileResult = selectedObj ? selectedObj : { title: "", id: "" }
        }
        if (profileKey === "fromTimeline") {
            setSelectedProfile((prevState: any) => ({
                ...prevState,
                [profileKey]: profileResult,
                toTimeline: {}
            }));
            setIndexOfFromTimeline(timelinesData.indexOf(profileResult))
        } else {
            setSelectedProfile((prevState: any) => ({
                ...prevState,
                [profileKey]: profileResult
            }));
        }
    };

    const handleChangeIsEntryLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormSubmitted(false)
        setSelectedProfile((prevState: any) => ({
            ...prevState,
            isEntryLevel: event.target.checked
        }));
    };


    // To set the dropdownitem once the country is selected from the dropdown
    const handleCountrySelect = async (event: any, currentTitle: string) => {
        const { value } = event.target
        var selectedItemsValue: DropdownItemType[] = [];
        if (currentTitle === "reset") {
            setSelectedCountries(selectedItemsValue);
        }
        else {
            if (currentTitle === "all") {
                selectedItemsValue = countriesList;
                setSelectedCountries(selectedItemsValue);
            } else {
                const selectedItems = selectedCountries;
                const itemAlreadySelected = selectedItems.map(function (e: any) { return e.title; }).indexOf(currentTitle)

                selectedItemsValue = (itemAlreadySelected !== -1) ? selectedItems.filter((e: any) => e.title !== currentTitle) : value;

                setSelectedCountries(selectedItemsValue);
            }
        }

        // In case any country of the region has been deselected or any additional country on the selected region has been selected, the region will be deselected
        setSelectedRegion(undefined);
        
        getProvinces(selectedItemsValue);
    };

    const fetchSubmittedData = async () => {
        setFormSubmitted(true)
        setLoading(true)
        setSelectedComponents(components);
        const hubIds = selectedCountries.length ? selectedCountries.map((e: any) => e.id) : [];
        const occupationCategoryIds = selectedProfile.occupation.length ? selectedProfile.occupation.map((e: any) => e.id) : [];
        const sectorIds = sectorsData && sectorsData.length ? sectorsData.map((e: any) => e.id) : [];
        const selectedProvincesObj = selectedProfile.provinces.length ? selectedProfile.provinces.map((e: any) => e.title) : [];
        const selectedFromTimeline = selectedProfile.fromTimeline ? selectedProfile.fromTimeline.title : "";
        const selectedToTimeline = selectedProfile.toTimeline ? selectedProfile.toTimeline.title : "";
        const Isced4Code = selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [];

        const [errTopOccupationsBySectors, incomingTopOccupationsBySectors] =
          await url.getTopOccupationsBySectors(
            hubIds,
            sectorIds,
            selectedProvincesObj,
            selectedFromTimeline,
            selectedToTimeline,
            selectedProfile.isEntryLevel,
          );
        if (errTopOccupationsBySectors)
          console.warn(errTopOccupationsBySectors);
        else
        {
          setTopOccupationsBySectors &&
            setTopOccupationsBySectors(incomingTopOccupationsBySectors);
        }

        const [
            errTopSectorAndOccupationInfo,
            incomingTopSectorAndOccupationInfo,
          ] = await url.getTopSectorAndOccupationInfo(
            hubIds,
            selectedProvincesObj,
            selectedFromTimeline,
            selectedToTimeline,
            selectedProfile.isEntryLevel,
          );
          if (errTopSectorAndOccupationInfo)
            console.warn(errTopSectorAndOccupationInfo);
          else
            setSectorAndOccupationInfo &&
              setSectorAndOccupationInfo(incomingTopSectorAndOccupationInfo);

        const [errTotalJobPosts, incomingTotalJobPosts] =
        await url.getTotalJobPosts(
            hubIds,
          sectorIds,
          occupationCategoryIds,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errTotalJobPosts) console.warn(errTotalJobPosts);
      if (setTotalJobPosts) setTotalJobPosts(incomingTotalJobPosts);
       

        if (selectedComponents.map((component) => component.title).includes("Top Employers")) {
            if (selectedProfile.sector.length === 0 && selectedProfile.occupation.length === 0 && selectedProfile.specializations.length === 0) {
                const [errTopEmployersByQuarter, incomingTopEmployersByQuarter] = await url.getTopEmployersByQuarter(hubIds, selectedProvincesObj, selectedFromTimeline, selectedToTimeline, selectedProfile.isEntryLevel);
                if (errTopEmployersByQuarter) console.warn(errTopEmployersByQuarter);
                setTopEmployers(incomingTopEmployersByQuarter)
                setTopEmployersTableCols([
                    {
                        key: 'employer',
                        label: 'Employer',
                    },
                    {
                        key: 'sectorName',
                        label: 'Sector',
                    },
                    {
                        key: 'jobCount',
                        label: 'Number of job posts',
                    },
                    {
                        key: 'percentage',
                        label: 'Market share',
                    },
                ])
            } else if (selectedProfile.specializations.length) {
                const [errTopEmployersByISCED4, incomingTopEmployersByISCED4] = await url.getTopEmployersByISCED4(hubIds, Isced4Code, selectedProvincesObj, selectedFromTimeline, selectedToTimeline, selectedProfile.isEntryLevel);
                if (errTopEmployersByISCED4) console.warn(errTopEmployersByISCED4);
                setTopEmployers(incomingTopEmployersByISCED4)
                setTopEmployersTableCols([
                    {
                        key: 'employer',
                        label: 'Top employer',
                    },
                    {
                        key: 'value',
                        label: 'Number of job posts',
                    }
                ])
            } else {
                const [errTopNEmployers, incomingTopNEmployers] = await url.getTopNEmployers(hubIds, sectorIds, occupationCategoryIds, selectedProvincesObj, selectedFromTimeline, selectedToTimeline, selectedProfile.isEntryLevel);
                if (errTopNEmployers) console.warn(errTopNEmployers);
                setTopEmployers(incomingTopNEmployers)
                setTopEmployersTableCols([
                    {
                        key: 'companyName',
                        label: 'Top Employer',
                    },
                    {
                        key: 'numJobs',
                        label: 'Number of job posts',
                    },
                ])
            }
        }

        if (selectedComponents.map((component) => component.title).includes("Tree Map")) {
            const [errTreeMapInfo, incomingTreeMapInfo] = await url.getTreeMapInfo(hubIds, selectedProvincesObj, selectedFromTimeline, selectedToTimeline, selectedProfile.isEntryLevel);
            if (errTreeMapInfo) console.warn(errTreeMapInfo);
            else setTreeMapInfo && setTreeMapInfo(incomingTreeMapInfo)
        }

        if (selectedComponents.map((component) => component.title).includes("Top Skills")) {
            const [errTopSkills, incomingTopSkills] = await url.getTopSkills(hubIds, sectorIds, occupationCategoryIds, [], selectedProvincesObj, selectedProfile.isEntryLevel);
            if (errTopSkills) console.warn(errTopSkills);
            else setTopSkills && setTopSkills(incomingTopSkills)
        }
        setLoading(false)

    }

    // Get Timeline according to the selected countries
    useEffect(() => {
        if (selectedCountries && selectedCountries.length > 0) {
            var tempTimelinesData: any[] = [];
            // Loop through all countries and get related timelines into a single timelines array
            selectedCountries.forEach((country: DropdownItemType) => {
                var timelinesByHubId = Timelines.find((timeline: any) => { return timeline.hubId === country.id });
                if (timelinesByHubId && timelinesByHubId.timelines)
                    tempTimelinesData = tempTimelinesData.concat(timelinesByHubId.timelines);
                else
                    tempTimelinesData = tempTimelinesData.concat(DefaultTimelines);

                // Remove duplicate timelines
                tempTimelinesData = [...new Set(tempTimelinesData)];
            });

            // Order timelines by converting quarter to quarter num
            if (tempTimelinesData && tempTimelinesData.length > 0) {
                var orderTimelinesData = tempTimelinesData.map((timeline: string) => {
                    var timelineSplitted = timeline.split(" ");
                    var quarterNum = timelineSplitted[1] + timelineSplitted[0].replace('Q', '');
                    return { quarter: timeline, quarterNum: parseInt(quarterNum) }
                });

                orderTimelinesData = orderTimelinesData.sort((a: any, b: any) => (a.quarterNum > b.quarterNum) ? 1 : -1);
                tempTimelinesData = orderTimelinesData.map((timeline: any) => {
                    return timeline.quarter
                });
                setTimelinesData && setTimelinesData(tempTimelinesData.map((e: string) => ({ id: e, title: e })))
            }
        }
    }, [selectedCountries, setTimelinesData]);

    // Used to prepare list of regions
    useEffect(() => {
        if (regionDetails && regionDetails.length > 0) {
            var regionsResult: DropdownItemType[] = [];
            regionDetails.forEach(regionDetail => {
                if (regionsResult.filter(x => x.id === regionDetail.id).length === 0)
                    regionsResult.push({
                        id: regionDetail.id,
                        title: regionDetail.name,
                    });
            });
            setRegionList && setRegionList(regionsResult);
        }
    }, [regionDetails, setRegionList]);

    // When countryList is loaded then set it under countries dropdown
    useEffect(() => {
        if (countryList && countryList.length) {
            var hubAccesses = getHubAccess();
            if (!hubAccesses || (hubAccesses && hubAccesses.length === 0))
                return;

            var tempCountryList = countryList.filter(value => { return hubAccesses.find((h: any) => h.hubId === value.id) });
            var filteredCountryList = tempCountryList.map(({ id, country }) => {
                return {
                    id,
                    title: country,
                }
            });
            setCountriesList(filteredCountryList);
        }
    }, [countryList]);

    // Used to set the new compare column options and call handleChangePreSelectedOptions which will update the values inside the parent component Compare.tsx
    useEffect(() => {
        if (newCompareColumnOptions.preSelectedProfile !== selectedProfile || newCompareColumnOptions.preSelectedRegion !== selectedRegion || newCompareColumnOptions.preSelectedCountries !== selectedCountries || newCompareColumnOptions.preSelectedOptions !== selectedOptions || newCompareColumnOptions.preSelectedComponents !== selectedComponents || newCompareColumnOptions.dropdownProvinces !== provinceList) {
            const options: CompareColumnOptionType = {
                preSelectedProfile: selectedProfile,
                preSelectedRegion: selectedRegion,
                preSelectedCountries: selectedCountries,
                preSelectedOptions: selectedOptions,
                preSelectedComponents: selectedComponents,
                dropdownProvinces: provinceList
            };
            setNewCompareColumnOptions(options);
            handleChangePreSelectedOptions && handleChangePreSelectedOptions(options);
        }
    }, [selectedProfile, selectedRegion, selectedCountries, selectedOptions, selectedComponents, provinceList, newCompareColumnOptions, handleChangePreSelectedOptions])


    const handleChangeSkillType = useCallback(() => {
        setSkillType((prevSkillsType: string) => (prevSkillsType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS));
    }, []);

    return (
        <div className="compare-column position-relative">
            <>
                <div className="mb-3">
                    <SelectMultiple
                        title="countries"
                        name="countries"
                        selectedItems={selectedCountries}
                        data={countriesList}
                        handleChange={handleCountrySelect}
                        style={{ minWidth: 360 }}
                    />
                </div>
                {selectedCountries && selectedCountries.length > 0 && <>
                    {loadProvinces ? <img src={LoaderImage} alt="" className="loadingImg" style={{ width: 40, position: "relative", top: "2%" }} /> : <div>

                      {selectedCountries && 
                      <>
                            <div className="mb-2 row">
                                <div className="col-6">
                                    <SelectSingle
                                        title="From"
                                        name="fromTimeline"
                                        selectedItem={selectedProfile.fromTimeline ? selectedProfile.fromTimeline.id : ""}
                                        data={timelinesData}
                                        handleChange={handleChangeTimeline}
                                    />
                                </div>
                                <div className="col-6">
                                    <SelectSingle
                                        title="To"
                                        name="toTimeline"
                                        selectedItem={selectedProfile.toTimeline ? selectedProfile.toTimeline.id : ""}
                                        data={timelinesData?.filter((e, index) => index >= indexOfFromTimeline)}
                                        handleChange={handleChangeTimeline}
                                    />
                                </div>
                            </div>
                            {/* <div className="mb-2 row">
                                <FormControlLabel
                                    className="switch-entry-level-label"
                                    control={
                                        <Switch
                                            checked={selectedProfile.isEntryLevel}
                                            onChange={handleChangeIsEntryLevel}
                                            className="switch-entry-level"
                                        />
                                    }
                                    label="Entry Level"
                                />
                            </div> */}
                            </>
                    }
                        {selectedOptions ? <>
                            <div className="mb-3">
                                <SelectMultiple
                                    title="options"
                                    selectedItems={selectedComponents}
                                    data={components}
                                    handleChange={handleChangeComponents}
                                />
                            </div>
                        </> : null}
                        <div className="d-flex justify-content-end mb-2">
                            {selectedProfile.fromTimeline.id !== "" && selectedProfile.toTimeline.id !== "" ? (
                                <button type="button" onClick={fetchSubmittedData} className="button button_with-icon">
                                    Run <ArrowForwardIos style={{ width: 30, float: "right" }} />
                                </button>
                            ) : null}
                        </div>
                    </div>}
                </>}
            </>
            {formSubmitted === true ? <div className="position-relative">
                {loading === true ? <img src={LoaderImage} alt="" className="loadingImg" style={{ width: 40 }} /> :
                    <div className="components">
                         <div className="widget-component">
                        <div className="d-flex total-job-post align-items-center">
                                            <h1>{totalJobPosts && totalJobPosts?.total_job_posts_count}</h1>
                                            <h3>Total job posts for the selected period</h3>
                                        </div>
                                        </div>

                        {topOccupationsBySectors && topOccupationsBySectors.length > 0 ? <>
                       
                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2">Top Occupations</h4>
                                <div>
                                    <Table id="topOccupations" columns={topOccupationsBySectorsTableCols} data={topOccupationsBySectors.map(e => {
                                        e.percentage = typeof (e.percentage) === "number" ? e.percentage + "%" : e.percentage;
                                        return e;
                                    })} />
                                </div>
                            </div>
                    </> : null}

                    {sectorAndOccupationInfo && sectorAndOccupationInfo.sectors != null && sectorAndOccupationInfo.sectors.length > 0 ?
                        <div className="widget-component">
                                                     
                                     <h4 className="border-bottom mb-2 pb-2">Top Sectors</h4>
                                     <Table 
                                            key={`topEmployers${index}`} 
                                            columns={[
                                                { key: 'slNo', label: 'SlNo' },
                                                { key: 'name', label: 'Sector' }
                                            ]}
                                            data={sectorAndOccupationInfo.sectors.map((sec, idx) => ({ 
                                                slNo: idx + 1, 
                                                name: sec.name 
                                            }))} 
                                            />
                                    
                       
                                </div>
                           : null}

                    {(selectedComponents.map((component) => component.title).includes("Top Employers")) ? <div className="mb-3">
                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2">Top Employers</h4>
                                {topEmployers && topEmployers.length ?
                                    <>
                                        {(selectedProfile.sector.length && selectedProfile.occupation.length && selectedProfile.specializations.length)
                                            ? <Table key={`topEmployers${index}`} columns={topEmployersTableCols} data={topEmployers} />
                                            : <Table key={`topEmployers${index}`} columns={topEmployersTableCols} data={topEmployers.map(e => {
                                                e.percentage = (e.percentage && typeof (e.percentage) === "number") ? e.percentage.toFixed(1) + "%" : e.percentage;
                                                return e;
                                            })} />}
                                    </> : <div className="na">N/A</div>
                                }
                            </div>
                        </div> : null}
                       
                        {/* {(selectedComponents.map((component) => component.title).includes("Tree Map")) ? <div className="mb-3">
                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2">Treemap</h4>
                                {treeMapInfo && ((treeMapInfo.sectors && treeMapInfo.sectors.length) || (treeMapInfo.occupations && treeMapInfo.occupations.length)) ? <div className="col-sm-12">

                                    {treeMapInfo.sectors.length &&
                                    
                                        <div className="mb-3">
                                            <TreemapChart name={`Sector${index}`} data={treeMapInfo.sectors} />
                                        </div>
                                    }
                                    {treeMapInfo.occupations.length &&
                                        <div className="mb-3">
                                            <TreemapChart name={`Occupation${index}`} data={treeMapInfo.occupations} />
                                        </div>
                                    }
                                </div>
                                    : <div className="na">N/A</div>
                                }
                            </div>
                        </div> : null}           */}


                        {(selectedComponents.map((component) => component.title).includes("Top Skills")) ? <div className="mb-3">
                            <div className="widget-component">
                                <h4 className="border-bottom mb-2 pb-2 occupation-tab__top-skills--header">
                                    {skillType === SkillTypes.WS ?
                                        <span>Top {topSkillGroup + "s"}</span>
                                        :
                                        <span>Top Skills</span>
                                    }
                                    {process.env.REACT_APP_ESCO_ENABLED === "true" &&
                                        <Button label={skillType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS} onClick={handleChangeSkillType} style={{ display: 'none' }}  />
                                    }
                                </h4>
                                {topSkills && topSkills.length ?
                                    <div>
                                        <div>
                                            {skillType === SkillTypes.WS ?
                                                <>
                                                    <HistoryEduIcon className="bar-toggle-icon" id="topSkillsCertification-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.Certification)} />
                                                    <AutoFixHighIcon className="bar-toggle-icon" id="topSkillsSoftSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.SoftSkill)} />
                                                    <BoltIcon className="bar-toggle-icon" id="topSkillsHardSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.HardSkill)} />
                                                </>
                                                :
                                                <div className="d-flex justify-content-center mt-2">
                                                    
                                                </div>
                                            }
                                        </div>
                                        <div id={"topSkills"} className="occupation-tab__top-skills mb-3">
                                            <TopSkills topSkills={topSkills.find(ts => (skillType === SkillTypes.WS ? ts.skillGroup === topSkillGroup : ts.skillGroup === SkillTypes.ESCO))?.topSkills || []} skillType={skillType} />
                                        </div>
                                    </div>
                                    :
                                    <div className="na">N/A</div>
                                }
                            </div>
                        </div> : null}

                    </div>}
            </div> : <></>}
        </div>
    );
};

export default CompareColumnNew;
